import { ArchivedBranchBanner, SuspenseLoader, TabsMenu } from '@folio/components';
import { stopDraftResumePath } from '@folio/funnels/Stop/resume-step';
import { useStopDraft } from '@folio/funnels/Stop/useStopDraft';
import { useAssertedParams } from '@folio/lib';
import { PolicyWarningBanner } from '@folio/modules';
import { PolicyEmailsTabLabel } from '@folio/modules/PolicyExchangesModule/policyEmailThreads';
import { useSendableNotificationReasons } from '@folio/components/features/notifications/useSendableNotificationReasons';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { PolicyTimeZoneProvider } from '@folio/utils/time-zone';
import { useFeatureFlag, type PolicyDashboardPage, type PolicyQuickAction } from 'korint-sdk/react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { PolicyDashboardHeader } from './PolicyDashboardHeader';
import { Stack } from '@folio/components/ui/flex';

export function PolicyDashboardLayout({
  pages,
  children,
  stopFunnelBasePath,
  quickActions,
}: {
  pages: ReadonlyArray<PolicyDashboardPage>;
  children: React.ReactNode;
  stopFunnelBasePath?: string;
  quickActions?: ReadonlyArray<PolicyQuickAction>;
}) {
  const { policyId, branchId } = useAssertedParams();
  const { t } = useTranslation();
  const basePath = `/policies/${policyId}${branchId ? `/${branchId}` : ''}`;

  const { policy, policyOnMain, workingBranch, isBranchListStale } = usePolicyOnDashboard();
  const isStopBranch = workingBranch?.branchType === 'STOP';
  const { draft, isLoading: isDraftLoading } = useStopDraft(policyId, {
    enabled: isStopBranch && !!stopFunnelBasePath,
  });
  const sendableReasons = useSendableNotificationReasons(policy.productId);

  const emailEnabled = useFeatureFlag('feat_connected_email_accounts');
  const tabs = pages
    .filter(page => page !== 'exchanges' || emailEnabled)
    .filter(page => page !== 'notifications' || sendableReasons.length > 0)
    .map(page => {
      if (typeof page === 'string') {
        return {
          label:
            page === 'exchanges' ? (
              <PolicyEmailsTabLabel policyId={policyId} />
            ) : (
              t(`policyDashboardPages.${page}`)
            ),
          href: `${basePath}${page === 'home' ? '' : `/${page}`}`,
        };
      }
      const customId = page.path.replace(/^\//, '');
      return {
        label: t(`policyDashboardPages.${customId}`),
        href: `${basePath}${page.path ? page.path : ''}`,
      };
    });

  // Unknown branch type until the refetch lands; the tabs would strand a just-started stop
  if (isBranchListStale) {
    return null;
  }

  // A STOP branch holds only a reason and a date: the dashboard's tabs have nothing to show for
  // it, so an unfinished stop goes back to its funnel and a scheduled one to the policy. Once it
  // carries a proof to review, the tabs are the manager's review surface and must render.
  if (isStopBranch && stopFunnelBasePath && !policyOnMain.isStopAwaitingValidation) {
    // Navigate fires once and never re-runs: redirecting before the draft resolves would read no
    // stopDate and send a dated draft back to the date step. Same gate as StopFunnelGuard.
    if (isDraftLoading) {
      return null;
    }

    return (
      <Navigate
        replace
        to={
          policyOnMain.stop
            ? `${stopFunnelBasePath}/${policyId}`
            : stopDraftResumePath({ funnelBasePath: stopFunnelBasePath, policyId, draft })
        }
      />
    );
  }

  return (
    <PolicyTimeZoneProvider>
      <Stack>
        <PolicyDashboardHeader
          stopFunnelBasePath={stopFunnelBasePath}
          quickActions={quickActions}
        />
        <ArchivedBranchBanner />
        <PolicyWarningBanner stopFunnelBasePath={stopFunnelBasePath} />
        <TabsMenu tabs={tabs} />
        <SuspenseLoader>{children}</SuspenseLoader>
      </Stack>
    </PolicyTimeZoneProvider>
  );
}
